import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo
} from "react";
import {
  useAccount,
  useChainId,
  useBalance,
  useEnsName,
  useEnsAvatar,
  useReadContracts,
  usePublicClient,
  useWalletClient,
  useDisconnect
} from "wagmi";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import { useSelector } from "react-redux";
import { userProfileUpdateApi } from "../api/methods-marketplace";
import { getUser } from "../redux/reducer/user_reducer";
import { addToast } from "@heroui/react";
import { formatWalletAddress } from "../utils/common";

const WalletContext = createContext();

const ERC20_ABI = [
  {
    constant: true,
    inputs: [{ name: "_owner", type: "address" }],
    name: "balanceOf",
    outputs: [{ name: "balance", type: "uint256" }],
    type: "function"
  },
  {
    constant: true,
    inputs: [],
    name: "decimals",
    outputs: [{ name: "", type: "uint8" }],
    type: "function"
  }
];

export function WalletProvider({ children }) {
  const user = useSelector(getUser);
  const { address, isConnected, isDisconnected } = useAccount();
  const chainId = useChainId();
  const isCustodialWallet = user?.is_custodial_wallet;
  const userAddress = user?.address;
  const resolvedAddress = isCustodialWallet ? userAddress : address;
  const { openConnectModal } = useConnectModal();
  const { disconnect } = useDisconnect();
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();

  const { data: ensName } = useEnsName({ address: resolvedAddress, chainId });
  const { data: ensAvatar } = useEnsAvatar({ name: ensName });

  // Native balance for connected wallets
  const {
    data: nativeBalanceData,
    isLoading: nativeBalanceLoading,
    refetch: refetchNativeBalance
  } = useBalance({
    address: resolvedAddress,
    watch: true,
    enabled: !isCustodialWallet && isConnected && !!resolvedAddress
  });

  // Native balance for custodial wallets
  const {
    data: custodialNativeBalanceData,
    isLoading: custodialNativeBalanceLoading,
    refetch: refetchCustodialNativeBalance
  } = useBalance({
    address: userAddress,
    watch: true,
    enabled: isCustodialWallet && !!userAddress
  });

  // BAF balance for connected wallets
  const {
    data: bafBalanceData,
    isLoading: bafBalanceLoading,
    error: bafBalanceError,
    refetch: refetchBAFBalance
  } = useBalance({
    address: resolvedAddress,
    token: process.env.REACT_APP_BAF_TOKEN_ADDRESS,
    watch: true,
    enabled: !isCustodialWallet && isConnected && !!resolvedAddress
  });

  // BAF balance for custodial wallets
  const {
    data: custodialBAFBalanceData,
    isLoading: custodialBAFBalanceLoading,
    error: custodialBAFBalanceError,
    refetch: refetchCustodialBAFBalance
  } = useBalance({
    address: userAddress,
    token: process.env.REACT_APP_BAF_TOKEN_ADDRESS,
    watch: true,
    enabled: isCustodialWallet && !!userAddress
  });

  const [tokenBalances, setTokenBalances] = useState({
    eth: "0",
    dai: "0",
    usdt: "0",
    tbnb: "0",
    wbnb: "0",
    bnb: "0"
  });

  const tokenAddresses = useMemo(
    () => ({
      eth: process.env.REACT_APP_ETH_TESTNET,
      dai: process.env.REACT_APP_DAI_TESTNET,
      usdt: process.env.REACT_APP_USDT_TESTNET,
      wbnb: process.env.REACT_APP_WBNB_TESTNET,
      tbnb: process.env.REACT_APP_WBNB_TESTNET
    }),
    []
  );

  const tokenContracts = useMemo(() => {
    if (!resolvedAddress) return [];
    return Object.entries(tokenAddresses).flatMap(([key, tokenAddress]) => {
      if (!tokenAddress) return [];
      return [
        {
          address: tokenAddress,
          abi: ERC20_ABI,
          functionName: "balanceOf",
          args: [resolvedAddress]
        },
        {
          address: tokenAddress,
          abi: ERC20_ABI,
          functionName: "decimals"
        }
      ];
    });
  }, [tokenAddresses, resolvedAddress]);

  const { data: contractResults, refetch: refetchTokenBalances } =
    useReadContracts({
      contracts: tokenContracts,
      enabled: (isCustodialWallet || isConnected) && !!resolvedAddress,
      watch: true
    });

  const formatBalance = useCallback((balance, decimals) => {
    if (!balance || !decimals) return "0";

    try {
      const bn = balance.toString();
      const dec = Number(decimals);

      if (bn.length <= dec) {
        const padded = bn.padStart(dec + 1, "0");
        return parseFloat(padded.slice(0, 1) + "." + padded.slice(1))
          .toFixed(6)
          .replace(/\.?0+$/, "");
      }

      const integerPart = bn.slice(0, -dec);
      const decimalPart = bn.slice(-dec);
      return parseFloat(integerPart + "." + decimalPart)
        .toFixed(6)
        .replace(/\.?0+$/, "");
    } catch {
      return "0";
    }
  }, []);

  useEffect(() => {
    if (
      !contractResults ||
      (!isCustodialWallet && !isConnected) ||
      !resolvedAddress
    )
      return;

    try {
      const tokenKeys = Object.keys(tokenAddresses);
      const newBalances = {};

      tokenKeys.forEach((key, index) => {
        const balanceResult = contractResults[index * 2];
        const decimalsResult = contractResults[index * 2 + 1];

        if (
          balanceResult?.status === "success" &&
          decimalsResult?.status === "success"
        ) {
          newBalances[key] = formatBalance(
            balanceResult.result,
            decimalsResult.result
          );
        } else {
          newBalances[key] = "0";
        }
      });

      newBalances.tbnb = newBalances.wbnb;

      // Use the correct native balance based on wallet type
      const currentNativeBalance = isCustodialWallet
        ? custodialNativeBalanceData?.formatted
        : nativeBalanceData?.formatted;
      newBalances.bnb = currentNativeBalance || "0";

      setTokenBalances((prev) => {
        if (JSON.stringify(prev) === JSON.stringify(newBalances)) return prev;
        return newBalances;
      });
    } catch (err) {
      addToast({
        title: "Balance Error",
        description: "Failed to fetch token balances.",
        color: "danger",
        variant: "bordered"
      });
    }
  }, [
    contractResults,
    isConnected,
    isCustodialWallet,
    resolvedAddress,
    nativeBalanceData,
    custodialNativeBalanceData,
    tokenAddresses,
    formatBalance
  ]);

  const updateProfileIfNeeded = async () => {
    if (
      !address ||
      !isConnected ||
      user?.address ||
      !user?.address === undefined
    )
      return;

    if (!user?.address) {
      try {
        const formData = { user: { address } };
        const updateResult = await userProfileUpdateApi(formData);

        if (updateResult?.data?.address) {
          addToast({
            title: "Wallet Address Updated",
            description: `Your wallet address ${formatWalletAddress(
              updateResult.data.address
            )} has been associated with this account.`,
            color: "success",
            variant: "bordered"
          });
          window.location.reload();
        }
      } catch (err) {
        addToast({
          title: "Update Failed",
          description: `Failed to update wallet address: ${
            err?.response?.data?.error[0] || err.message
          }`,
          color: "error",
          variant: "bordered"
        });
        console.error(
          "❌ Failed to update wallet address:",
          err?.response?.data?.error[0] || err.message
        );
      }
    } else if (user?.address.toLowerCase() !== address.toLowerCase()) {
      addToast({
        title: "Wallet Address Mismatch",
        description: `The connected wallet ${formatWalletAddress(
          address
        )} does not match your registered address ${formatWalletAddress(
          user.address
        )}.`,
        color: "warning",
        variant: "bordered"
      });
      disconnectWallet();
    }
  };
  useEffect(() => {
    if (isConnected && address) {
      localStorage.setItem("triggeredWalletConnect", "true");
    }
  }, [isConnected, address]);

  useEffect(() => {
    const triggered = localStorage.getItem("triggeredWalletConnect") === "true";
    if (
      !triggered ||
      !isConnected ||
      !address ||
      user?.address ||
      user?.address === undefined
    )
      return;
    updateProfileIfNeeded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, address, user]);

  const hasAddressMismatch = useMemo(() => {
    if (isCustodialWallet || !isConnected || !address || !user?.address)
      return false;
    return user.address.toLowerCase() !== address.toLowerCase();
  }, [isCustodialWallet, isConnected, address, user?.address]);

  const connectWallet = useCallback(() => {
    if (openConnectModal) {
      localStorage.setItem("triggeredWalletConnect", "true");
      openConnectModal();
    }
  }, [openConnectModal]);

  const disconnectWallet = useCallback(() => {
    localStorage.removeItem("triggeredWalletConnect");
    disconnect();
  }, [disconnect]);

  const refreshBalance = useCallback(async () => {
    if (isCustodialWallet) {
      await refetchCustodialBAFBalance();
      await refetchCustodialNativeBalance();
    } else {
      await refetchBAFBalance();
      await refetchNativeBalance();
    }
  }, [
    isCustodialWallet,
    refetchBAFBalance,
    refetchNativeBalance,
    refetchCustodialBAFBalance,
    refetchCustodialNativeBalance
  ]);

  const refreshAllBalances = useCallback(async () => {
    if (isCustodialWallet) {
      await refetchCustodialBAFBalance();
      await refetchCustodialNativeBalance();
    } else {
      await refetchBAFBalance();
      await refetchNativeBalance();
    }
    await refetchTokenBalances();
  }, [
    isCustodialWallet,
    refetchBAFBalance,
    refetchNativeBalance,
    refetchCustodialBAFBalance,
    refetchCustodialNativeBalance,
    refetchTokenBalances
  ]);

  const activeBAFBalanceData = isCustodialWallet
    ? custodialBAFBalanceData
    : bafBalanceData;
  const activeBAFBalanceLoading = isCustodialWallet
    ? custodialBAFBalanceLoading
    : bafBalanceLoading;
  const activeBAFBalanceError = isCustodialWallet
    ? custodialBAFBalanceError
    : bafBalanceError;
  const activeNativeBalanceData = isCustodialWallet
    ? custodialNativeBalanceData
    : nativeBalanceData;

  const walletState = useMemo(
    () => ({
      isConnected: isCustodialWallet ? !!userAddress : isConnected,
      isDisconnected: isCustodialWallet ? !userAddress : isDisconnected,
      address: resolvedAddress,
      chainId,
      ensName,
      ensAvatar,
      bafBalance: activeBAFBalanceData?.value?.toString() || "0",
      bafBalanceFormatted: activeBAFBalanceData?.formatted || "0",
      bafBalanceSymbol: activeBAFBalanceData?.symbol || "",
      bafBalanceLoading: activeBAFBalanceLoading,
      bafBalanceError: activeBAFBalanceError,
      nativeBalance: activeNativeBalanceData?.value?.toString() || "0",
      nativeBalanceFormatted: activeNativeBalanceData?.formatted || "0",
      tokenBalances,
      hasAddressMismatch,
      publicClient,
      walletClient,
      connectWallet,
      disconnectWallet,
      refreshBalance,
      refreshAllBalances,
      isCustodialWallet
    }),
    [
      isCustodialWallet,
      userAddress,
      isConnected,
      isDisconnected,
      resolvedAddress,
      chainId,
      ensName,
      ensAvatar,
      activeBAFBalanceData,
      activeBAFBalanceLoading,
      activeBAFBalanceError,
      activeNativeBalanceData,
      tokenBalances,
      hasAddressMismatch,
      publicClient,
      walletClient,
      connectWallet,
      disconnectWallet,
      refreshBalance,
      refreshAllBalances
    ]
  );

  return (
    <WalletContext.Provider value={walletState}>
      {children}
    </WalletContext.Provider>
  );
}

export function useWallet() {
  const context = useContext(WalletContext);
  if (!context) throw new Error("useWallet must be used inside WalletProvider");
  return context;
}
